(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports) :
  typeof define === 'function' && define.amd ? define(['exports'], factory) :
  (global = global || self, factory(global.finance = {}));
}(this, (function (exports) { 'use strict';

  const COMMISSION_FLAG = obj => 'old_commission' in obj;

  const oldCommission = obj => !COMMISSION_FLAG(obj) || COMMISSION_FLAG(obj) && obj.old_commission; // Adds the rate to the value
  // e.g. (10, 12.5) => 11.25


  const addRate = (value, rate) => value * (1 + rate / 100); // Removes the rate from the value
  // e.g. (11.25, 12.5) => 10


  const removeRate = (value, rate) => value / (1 + rate / 100); // Adds the rate to the value, and then removes the initial value,
  // revealing the value of the rate
  // e.g. (10, 12.5) => 1.25


  const calculateRateValue = (value, rate) => addRate(value, rate) - value; // Removes the rate from the value, and then returns the difference
  // revealing the value of the rate removed
  // e.g. (11.25, 12.5) => 1.25


  const calculateRemovedRateValue = (value, rate) => value - removeRate(value, rate); // Rounds a value up or down to specified multiple
  // e.g. (7.6, 5) => 10 and (7.4, 5) => 5


  const roundToClosest = (value, rounding) => Math.round(value / rounding) * rounding; // Translates a pennies value to a value in pounds


  const toPounds = value => value / 100; // Translates a pound value to pennies


  const toPennies = value => value * 100; // Calculates booking protection price based on booking value and round up or down


  const calculateBookingProtectionValue = (value, fee, rounding) => toPennies(roundToClosest(calculateRateValue(toPounds(value), fee), rounding)); // Takes the base value (with commission) and adds service fee and protection amount
  // e.g. (1000, 12.5, 2.5, 5, 5) => 1208.13


  const calculateBaseToFull = (total, serviceFee, protectionFee = 0, protectionRounding = 0) => {
    const protectionAmount = calculateBookingProtectionValue(total, protectionFee, protectionRounding) || 0;
    return Number(addRate(total, serviceFee)) + protectionAmount;
  };

  const getVatPercent = (vatAmount, subTotal) => Math.round(vatAmount / subTotal * 100);

  const getPriceBeforeVat = (price, vatPercent) => price / (1 + vatPercent / 100);

  const formatCurrency = value => value.toFixed(2);

  const baseTotal = ({
    total = 0
  } = {}) => total || 0;

  const calculatePaidPercentage = (paidAmount, total) => Math.round(paidAmount / total * 100); // 2020 COMMISIONS MATH
  // (when given a supplier quote value and commission rate, calculate value of commission needed to be added to achieve that commission)
  // e.g. (100000, 12.5) => 14286 (12.5% of total 114286)
  // e.g (100000, 20) => 25000 (20% of total 125000)


  const calculateCommissionFee = (value, rate) => {
    if (rate < 0) {
      throw new Error('rate must be a positive number');
    }

    return Math.round(value / (1 - rate / 100) - value);
  }; // when given a total and a commission rate, calculate value of commission
  // e.g (125000, 20) => 25000 (20% of 125000)
  // e.g (114286, 12.5) => 14285.75 (12.5% of 114286)


  const calculateCommissionFromTotal = (total, rate) => {
    if (rate < 0) {
      throw new Error('rate must be a positive number');
    }

    return Math.round(total * (rate / 100));
  };

  const calculateBookingTotal = (value, rate) => {
    if (rate < 0) {
      throw new Error('rate must be a positive number');
    }

    return value + calculateCommissionFee(value, rate);
  }; // if old commission logic, commission needs to be added to total
  // e.g. ({ total: 10000, commission: 12.5 }) => 11250
  // e,g. ({ total: 10000, commission: 12.5, old_commission: true }) => 11250
  // e.g. ({ total: 11250, commission: 12.5, old_commission: false }) => 11250


  const calculateBaseTotal = obj => oldCommission(obj) ? addRate(baseTotal(obj), obj.commission_fee) : baseTotal(obj); // if new commission, commission needs to be removed from total for trader value
  // e.g. ({ total: 10000, commission_fee: 12.5, old_commission: false }) => 8750


  const calculateTraderValue = obj => oldCommission(obj) ? baseTotal(obj) : baseTotal(obj) - calculateCommissionFromTotal(baseTotal(obj), obj.commission_fee); // Construct common breakdown of an object such as a booking or a quote, entirely dependent on commission logic


  const constructCostBreakdown = obj => {
    const traderValue = calculateTraderValue(obj);
    const baseTotalValue = calculateBaseTotal(obj);
    const commissionValue = oldCommission(obj) ? calculateRateValue(traderValue, obj.commission_fee) : calculateCommissionFromTotal(baseTotalValue, obj.commission_fee);
    const serviceValue = calculateRateValue(baseTotalValue, obj.service_fee);
    const customerTotal = addRate(baseTotalValue, obj.service_fee);
    return {
      traderValue,
      baseTotal: baseTotalValue,
      commissionValue,
      serviceValue,
      customerTotal
    };
  };

  exports.COMMISSION_FLAG = COMMISSION_FLAG;
  exports.addRate = addRate;
  exports.baseTotal = baseTotal;
  exports.calculateBaseToFull = calculateBaseToFull;
  exports.calculateBaseTotal = calculateBaseTotal;
  exports.calculateBookingProtectionValue = calculateBookingProtectionValue;
  exports.calculateBookingTotal = calculateBookingTotal;
  exports.calculateCommissionFee = calculateCommissionFee;
  exports.calculateCommissionFromTotal = calculateCommissionFromTotal;
  exports.calculatePaidPercentage = calculatePaidPercentage;
  exports.calculateRateValue = calculateRateValue;
  exports.calculateRemovedRateValue = calculateRemovedRateValue;
  exports.calculateTraderValue = calculateTraderValue;
  exports.constructCostBreakdown = constructCostBreakdown;
  exports.formatCurrency = formatCurrency;
  exports.getPriceBeforeVat = getPriceBeforeVat;
  exports.getVatPercent = getVatPercent;
  exports.oldCommission = oldCommission;
  exports.removeRate = removeRate;
  exports.roundToClosest = roundToClosest;
  exports.toPennies = toPennies;
  exports.toPounds = toPounds;

  Object.defineProperty(exports, '__esModule', { value: true });

})));
